import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import DocPageLayout from "@/components/DocPageLayout";

const FAQ_GROUPS = [
  {
    group: "Getting Started",
    items: [
      {
        q: "What is Hunt57?",
        a: "Hunt57 is a private, invite-only marketplace for sports card collectors. Members list cards and browse cards; all sales happen directly between the buyer and the seller. Hunt57 itself is a listing service — we do not touch money, hold escrow, or arbitrate transactions.",
      },
      {
        q: "How do I get an account?",
        a: "Sign up for a free member account at /register. You'll get a browsing, watchlist, messaging, and buying account. Selling requires an additional upgrade (see 'Vendor Accounts' below).",
      },
      {
        q: "Is Hunt57 really free?",
        a: "Buying, browsing, watchlisting, and messaging are 100% free forever. Vendors (sellers) get a 30-day free trial. If they don't cancel within the trial window, they are charged a launch-promo $49.99 for their first year (regular price $69.99, $20 off first year). It renews annually at $69.99 on the anniversary of that initial charge. A payment method is required on file for the trial to begin. Founding Members are exempt from vendor fees for the life of their account.",
      },
      {
        q: "Do I need to verify my email?",
        a: "Not required to browse. It's strongly recommended if you plan to sell so we can reach you about disputes or account issues.",
      },
      {
        q: "Why an invite-only community?",
        a: "The hobby has a scammer problem. Invite-only means every member is vouched for or admin-approved. Fewer scams, better feedback, real reputations.",
      },
      {
        q: "What does the small gold top hat next to a username mean?",
        a: "The gold top hat marks a Founding Member — someone who joined Hunt57 during our launch cohort. Founding Members have a permanently discounted vendor account and appear across the site (Browse listings, seller cards, Cart, Purchase Orders, Messages) with the top-hat badge as a trust and history signal.",
      },
    ],
  },
  {
    group: "Buying",
    items: [
      {
        q: "How do I buy a card?",
        a: "On any listing detail page click 'Purchase This Card'. You'll be asked to acknowledge the Hunt57 Purchase Terms, then a Purchase Order (PO) is opened. The seller has 72 hours to validate. Once validated, you pay them directly using their listed payment method (PayPal / Cash App / Venmo). Both parties confirm completion in the Transactions page and the sale is logged.",
      },
      {
        q: "What is a Purchase Order (PO)?",
        a: "A PO is Hunt57's transaction record. It's opened when a buyer commits to a card, moves through validate → pay → complete → feedback, and expires automatically after 72 hours at each stage if action isn't taken. It exists to give both parties a paper trail and to feed the reputation system — Hunt57 never handles the money itself.",
      },
      {
        q: "Why do I see a FIRM chip on some listings?",
        a: "FIRM means the seller is not accepting offers on that card. Sending an offer on a FIRM listing is a community rule violation. If you do it anyway, the seller can report you and admins may issue a strike. Respect FIRM.",
      },
      {
        q: "Can I cancel a Purchase Order?",
        a: "Yes, up until the seller validates. Open the PO in /transactions and hit Cancel. After validation, cancellation typically requires seller cooperation — reach out via Messages first.",
      },
      {
        q: "What happens if the seller never ships?",
        a: "Confirm nothing until you have the card in hand. If a seller doesn't ship after payment, do NOT click Complete on the PO. Reach out via Messages, and if unresolved, report the seller. Repeated failures trigger strikes and possible removal.",
      },
      {
        q: "Does Hunt57 offer buyer protection?",
        a: "No. Hunt57 does not process payments, so there is no built-in refund flow. Buyer protection is entirely from your payment provider (PayPal Goods & Services, credit card chargebacks). This is stated at signup, purchase, and in the Terms — that's why we don't hide it.",
      },
      {
        q: "What is combined shipping?",
        a: "Some sellers offer a discount when you buy multiple cards from them in one shipment. Look for the blue COMBO chip. The listing detail page shows the initial charge, per-additional-card charge, and the maximum cards allowed in one package.",
      },
    ],
  },
  {
    group: "Selling",
    items: [
      {
        q: "How do I become a vendor?",
        a: "Two paths: (1) Apply for an admin-reviewed vendor account from Account → 'Apply to Sell'. Requires a valid payment method on file (Stripe). (2) Redeem a vendor referral code (HUNTV-XXXXXX) from a Tier 3 vendor in the same section for instant vendor status.",
      },
      {
        q: "What are the vendor tiers?",
        a: "Tier 1 (Starter): 25 active listings max, $50 max price. Tier 2 (Established): No listing limit, no price cap. Tier 3 (Trusted): No limits + can generate 3 referral vouchers per year. Progression is automatic based on confirmed sales and standing.",
      },
      {
        q: "Why do I need to add a credit card if the vendor trial is free?",
        a: "The card guarantees we can bill you when the 30-day free trial ends without disrupting your active listings. You can cancel any time during the trial from Account → Billing Portal — no charges apply. If you don't cancel by day 30, the first-year launch-promo charge of $49.99 fires (regular price $69.99), and the same date becomes your annual anniversary going forward.",
      },
      {
        q: "How do I create a listing?",
        a: "Sell Card → Fill in card details, upload front + back photos, set price, and choose whether to accept offers. Bulk upload is available at Sell → Bulk Upload for uploading multiple cards from a spreadsheet.",
      },
      {
        q: "What is combined shipping and how do I offer it?",
        a: "Account → Combined Shipping section. Toggle it on and set: (1) initial charge, (2) per-additional-card charge, (3) max cards per package. The policy displays on every one of your listings automatically. Once buyers hit the max, a new full shipping charge starts.",
      },
      {
        q: "How does the strike system work?",
        a: "Strikes accumulate for community violations (offers on FIRM listings, ghosting on POs, reported behavior). 0 strikes = Excellent standing (3 green checks). 1-2 strikes = Fair. 3+ strikes = Poor + risk of suspension. See Trust & Safety for full details.",
      },
      {
        q: "Can buyers see my sales history?",
        a: "Your public profile at /u/YOUR_USERNAME shows your confirmed sale count, feedback ratings, and current standing. It's a badge of legitimacy — good vendors want it visible.",
      },
    ],
  },
  {
    group: "Messaging & Reports",
    items: [
      {
        q: "How do I message another user?",
        a: "On any listing detail page click 'Message'. First message opens a conversation thread visible in /messages. All messaging is one-to-one — no groups.",
      },
      {
        q: "How do I report an abusive message?",
        a: "Hover any incoming message in a conversation and click the small flag icon. Choose a reason (harassment, FIRM-offer, scam, spam, etc.) and type a required comment (5+ chars). Admins review reports and can strike or suspend the offender.",
      },
      {
        q: "Can I archive old conversations?",
        a: "Yes. Open a thread and click Archive in the header — it moves the thread out of your Inbox into Archived. You can unarchive at any time.",
      },
    ],
  },
  {
    group: "Payments & Money",
    items: [
      {
        q: "Does Hunt57 process card-sale payments?",
        a: "No. Every card sale is a private off-platform transaction. Buyers pay sellers directly via the seller's listed method (PayPal, Cash App, Venmo, Zelle). Hunt57 never touches funds, never escrows, never refunds. This is a legal and technical fact — see Terms.",
      },
      {
        q: "What does Hunt57 charge me for?",
        a: "Only the vendor subscription — 30-day free trial, then a launch-promo $49.99 for the first year (regular $69.99, $20 off first year). Renews at $69.99/year on the anniversary of your initial charge date. Processed by Stripe (we don't store card details). Buyers are never charged anything by Hunt57.",
      },
      {
        q: "What if a payment fails?",
        a: "That's between you and your payment provider. Hunt57 has no visibility into off-platform payment flows. If a buyer says they paid but the seller says they didn't, both parties should share receipts in Messages — but Hunt57 does not arbitrate.",
      },
      {
        q: "Are there any fees on the sale itself?",
        a: "None from Hunt57 — that's the whole model. You may pay small fees to your payment provider (PayPal G&S, Stripe, etc.). Those are outside our control.",
      },
    ],
  },
  {
    group: "Account & Privacy",
    items: [
      {
        q: "Can I change my username?",
        a: "Contact support if you need a username change. Your username powers your public profile URL so we handle it manually to avoid broken links.",
      },
      {
        q: "How do I delete my account?",
        a: "Contact support and request deletion. Your listings and messages will be removed; sales history stays anonymized in the reputation system.",
      },
      {
        q: "Do you sell my data?",
        a: "No. Hunt57 stores only the info you provide (username, email, listings, messages). We do not sell personal data. See Privacy Policy for the full list of what we store and how.",
      },
    ],
  },
  {
    group: "Troubleshooting",
    items: [
      {
        q: "I can't log in — password reset isn't working.",
        a: "Try clearing your browser cache, then retry. If still stuck, contact support via /contact with your registered email and we'll manually reset it.",
      },
      {
        q: "Images aren't loading.",
        a: "Usually a browser cache or ISP hiccup. Hard-refresh (Ctrl+Shift+R / Cmd+Shift+R). If persistent, contact support with a screenshot.",
      },
      {
        q: "I hit an error I can't explain.",
        a: "Take a screenshot of the error + browser console (F12 → Console tab), open /contact, and submit a Bug report. Include what you were doing when it happened.",
      },
    ],
  },
];

function FAQItem({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`faq-question-${q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
        className="w-full text-left flex items-center justify-between gap-4 px-4 py-3 hover:bg-white/5 transition-colors"
      >
        <span className="font-display font-bold text-[15px] leading-snug">{q}</span>
        <CaretDown
          size={14}
          weight="bold"
          className={`text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180 text-[#FF5F1F]" : ""}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-4 text-[14px] leading-relaxed text-zinc-300 border-t border-white/5 pt-3">
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <DocPageLayout
      subtitle="Help"
      title="Frequently Asked"
      intro="Everything you need to know about buying, selling, and being part of the Hunt57 community. Can't find your answer? Hit the Contact Us page — but only for technical issues, please."
    >
      {FAQ_GROUPS.map((group) => (
        <section key={group.group} data-testid={`faq-group-${group.group.replace(/\s+/g, "-").toLowerCase()}`}>
          <h2 className="font-display font-black uppercase text-xl tracking-tight text-[#FF5F1F] mb-3">
            → {group.group}
          </h2>
          <div className="space-y-2">
            {group.items.map((it) => (
              <FAQItem key={it.q} q={it.q} a={it.a} />
            ))}
          </div>
        </section>
      ))}
    </DocPageLayout>
  );
}
